import React from 'react';
import RowHeader from './RowHeader';
import ListingCard from './ListingCard';
import { HSCROLL } from './tokens';
import { freshnessTag, savedKeyFromView, type HomeListingView } from './listingView';
import { composeNewThisWeek } from './marketplaceFirst';
import type { SavedTarget } from '../../../lib/api/savedTargets';

// Moved to the pure module so it can be tested under plain `npx tsx` (this file
// cannot be: ListingCard reaches lib/api/client.ts). Re-exported for callers
// that still import it from here.
export { selectNewThisWeek } from './marketplaceFirst';

/**
 * HOME-01 / SYND-18 — "New this week", the first row inside Discover.
 *
 * TWO BLOCKS, MARKETPLACE FIRST. The hold-out that kept indexed rows out of
 * this row is replaced by PO decision D2 (2026-10-13). The row is now the
 * week's agent listings first (at most five, newest first), then the week's
 * indexed additions filling it to eight, at most two per source. The
 * composition lives in marketplaceFirst.ts; read its header before changing
 * any of those numbers.
 *
 * WHY THE BLOCKS NEVER INTERLEAVE. A marketplace card is an agent's listing
 * with a contact route behind it. An indexed card is a fact we found on
 * someone else's page, with no contact route. Sorting them into one list by
 * date would let a single batch ingest, which stamps a whole source in one
 * moment, push every agent listing of the week out of the row. So kind fixes
 * the order between the blocks, and date orders cards only inside each block.
 *
 * TWO DATES, TWO VERBS. A marketplace card's date is the listing's own
 * `created_at`: "Listed 2 days ago". An indexed card's date is `indexed_at`,
 * which is when WE added it and not when it came to market: "Added 2 days ago".
 * `freshnessTag` picks the verb from `source`, so the row never states a fact
 * about someone else's listing that is not ours to state.
 *
 * THE ROW COLLAPSES only when both blocks are empty. The alternative, falling
 * back to "the newest listings we have", would put a three-month-old home
 * under a heading that says this week, which is the one thing a freshness row
 * must never do.
 */

interface NewThisWeekRowProps {
  /** Marketplace listings, any age. The row applies the window itself. */
  marketplace: HomeListingView[];
  /**
   * Indexed rows from `syndicated_new_this_week`, which is already windowed and
   * capped. The row windows them again and caps per source again, on purpose.
   */
  indexed: HomeListingView[];
  /** SYND-15: keyed `${kind}:${id}` — see savedKeyFromView, never a bare uuid. */
  savedIds: ReadonlySet<string>;
  /** SYND-15: the card composes a SavedTarget from its own `source`. */
  onSave: (target: SavedTarget, e: React.MouseEvent) => void;
  viewAllHref?: string;
  /** Injectable for tests; defaults to now. */
  now?: number;
}

const NewThisWeekRow: React.FC<NewThisWeekRowProps> = ({
  marketplace,
  indexed,
  savedIds,
  onSave,
  viewAllHref,
  now = Date.now(),
}) => {
  const fresh = composeNewThisWeek(marketplace, indexed, now);
  if (fresh.length === 0) return null;

  return (
    <section aria-labelledby="home-new-this-week">
      <RowHeader title="New this week" viewAllHref={viewAllHref} className="mb-2.5 lg:mb-3.5" />
      <div className={HSCROLL}>
        {fresh.map((listing) => (
          <ListingCard
            /* SYND-15: kind-prefixed, like every other key over these two id
               spaces (unifiedResultKey, savedIds). This row holds both kinds
               since SYND-18, so a bare uuid could collide across the two
               tables and silently drop a card. */
            key={savedKeyFromView(listing)}
            listing={listing}
            variant="compact"
            saved={savedIds.has(savedKeyFromView(listing))}
            onSave={onSave}
            freshLabel={freshnessTag(listing.datedAt, listing.source, now)}
          />
        ))}
      </div>
    </section>
  );
};

export default NewThisWeekRow;
